import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import { useAppSelector } from '@/redux/hooks';
import { getDeployment } from '@/redux/modules/config';
import { track } from '@/utils/amplitude/analytics/core';
import { TrackingEvents, TrackingPayload } from '@/utils/amplitude/analytics/core.types';
import { getFullUrl } from '@/utils/amplitude/analytics/utils/getFullUrl';
import { getAmplitudePageNameFromPath } from '@/utils/getPageNameFromPath';

export type LeadingBidderModalNotificationsStatus = {
    browserNotificationsEnabled: boolean;
    smsNotificationsEnabled: boolean;
};

const trackNotificationsCTAClicked = (payload: TrackingPayload) => {
    track(TrackingEvents.CTA.CTAClicked, payload);
};

export const useTrackBrowserNotificationsClicked = () => {
    const deployment = useAppSelector(getDeployment);
    const { pathname } = useLocation();

    return useCallback(() => {
        trackNotificationsCTAClicked({
            buttonAction: 'Turns on Browser Notifications',
            CTAName: 'Enable Browser Notifications',
            eventTrigger: 'Enable Browser Notification Banner',
            pageName: getAmplitudePageNameFromPath(pathname),
            url: getFullUrl(deployment, pathname),
        });
    }, [deployment, pathname]);
};

export const useTrackOutbidNotificationsClicked = () => {
    const deployment = useAppSelector(getDeployment);
    const { pathname } = useLocation();

    return useCallback(
        (outBidNotificationActive: boolean) => {
            trackNotificationsCTAClicked({
                buttonAction: outBidNotificationActive ? 'Outbid Notification Enabled' : 'Outbid Notification Disabled',
                CTAName: "Notify Me When I'm Outbid",
                eventTrigger: 'User is leading bidder after submitting bid',
                pageName: getAmplitudePageNameFromPath(pathname),
                url: getFullUrl(deployment, pathname),
            });
        },
        [deployment, pathname]
    );
};

export const useTrackLeadingBidderModalSaveClicked = () => {
    const deployment = useAppSelector(getDeployment);
    const { pathname } = useLocation();

    return useCallback(
        (notificationsStatus: LeadingBidderModalNotificationsStatus) => {
            trackNotificationsCTAClicked({
                buttonAction: 'Saves User Notification Preferences',
                CTAName: 'Save',
                eventTrigger: 'User submits a leading bid',
                notificationsEnabled: notificationsStatus,
                pageName: getAmplitudePageNameFromPath(pathname),
                url: getFullUrl(deployment, pathname),
            });
        },
        [deployment, pathname]
    );
};

export const useTrackLeadingBidderExperimentToastClicked = () => {
    const deployment = useAppSelector(getDeployment);
    const { pathname } = useLocation();

    return useCallback(() => {
        trackNotificationsCTAClicked({
            buttonAction: 'Re-directs user to Email and Notifications settings page',
            CTAName: 'Manage Settings',
            eventTrigger: 'User updated their notification preferences',
            pageName: getAmplitudePageNameFromPath(pathname),
            url: getFullUrl(deployment, pathname),
        });
    }, [deployment, pathname]);
};
